/**
 * Bakehouse Wallet Registration
 * Handles the registration form submission, wallet type detection,
 * and integration with the backend API to create Recharge customers
 * and return Apple/Google wallet passes.
 */

class BakehouseWalletRegistration extends HTMLElement {
  constructor() {
    super();
    this.walletPassUrl = null;
  }

  connectedCallback() {
    // Wait for next tick to ensure DOM is ready
    requestAnimationFrame(() => {
      this.initElements();
      this.init();
    });
  }

  initElements() {
    this.form = this.querySelector('form');
    this.statusContainer = this.querySelector('.bakehouse-wallet__status');
    this.successContainer = this.querySelector('.bakehouse-wallet__success');
    this.successMessage = this.querySelector('.bakehouse-wallet__success-message');
    this.submitButton = this.querySelector('.bakehouse-wallet__submit');
    this.submitText = this.querySelector('.bakehouse-wallet__submit-text');
    this.submitLoading = this.querySelector('.bakehouse-wallet__submit-loading');
    this.walletTypeInput = this.querySelector('input[name="wallet_type"]');
    this.addWalletButton = this.querySelector('.bakehouse-wallet__add-wallet');
    this.walletText = this.querySelector('.bakehouse-wallet__wallet-text');
    this.walletIcon = this.querySelector('.bakehouse-wallet__wallet-icon');
    
    this.apiEndpoint = this.dataset.apiEndpoint || '';
    this.walletType = this.detectWalletType();
  }

  init() {
    if (!this.form) {
      console.error('BakehouseWalletRegistration: Form element not found');
      return;
    }

    // Set the detected wallet type in the hidden field
    if (this.walletTypeInput) {
      this.walletTypeInput.value = this.walletType;
    }

    // Bind form submission
    this.form.addEventListener('submit', this.handleSubmit.bind(this));

    // Add input validation listeners
    this.form.querySelectorAll('input[required]').forEach(input => {
      input.addEventListener('blur', () => this.validateField(input));
      input.addEventListener('input', () => this.clearFieldError(input));
    });
    
    console.log('BakehouseWalletRegistration initialized', {
      walletType: this.walletType,
      apiEndpoint: this.apiEndpoint
    });
  }

  /**
   * Detects whether the user is on an iOS device (Apple Wallet)
   * or Android device (Google Wallet)
   * @returns {string} 'apple' | 'google' | 'unknown'
   */
  detectWalletType() {
    const userAgent = navigator.userAgent || navigator.vendor || window.opera;
    
    // iOS detection
    if (/iPad|iPhone|iPod/.test(userAgent) && !window.MSStream) {
      return 'apple';
    }
    
    // macOS Safari detection (for testing on Mac)
    if (/Macintosh/.test(userAgent) && 'ontouchend' in document) {
      return 'apple';
    }
    
    // Android detection
    if (/android/i.test(userAgent)) {
      return 'google';
    }
    
    // Fallback: check for platform
    if (navigator.platform) {
      if (['iPhone', 'iPad', 'iPod'].includes(navigator.platform)) {
        return 'apple';
      }
    }
    
    // Default to 'unknown' - the server can decide or show both options
    return 'unknown';
  }

  /**
   * Validates a single form field
   * @param {HTMLInputElement} field 
   * @returns {boolean}
   */
  validateField(field) {
    const value = field.value.trim();
    let isValid = true;
    let errorMessage = '';

    // Check required
    if (field.hasAttribute('required') && !value) {
      isValid = false;
      errorMessage = 'This field is required';
    }

    // Check email format
    if (isValid && field.type === 'email' && value) {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(value)) {
        isValid = false;
        errorMessage = 'Please enter a valid email address';
      } else {
        const localPart = value.split('@')[0];
        if (localPart.startsWith('.') || localPart.endsWith('.') || localPart.includes('..')) {
          isValid = false;
          errorMessage = 'Please enter a valid email address';
        }
      }
    }

    // Check phone format
    if (isValid && field.type === 'tel' && value) {
      // Remove formatting to count digits
      const digitsOnly = value.replace(/[^\d]/g, '');
      if (digitsOnly.length < 10) {
        isValid = false;
        errorMessage = 'Please enter a valid phone number (at least 10 digits)';
      }
    }

    // Check checkbox
    if (field.type === 'checkbox' && field.hasAttribute('required') && !field.checked) {
      isValid = false;
      errorMessage = 'You must accept the terms to continue';
    }

    // Show/hide error
    if (!isValid) {
      this.showFieldError(field, errorMessage);
    } else {
      this.clearFieldError(field);
    }

    return isValid;
  }

  /**
   * Shows an error message for a field
   * @param {HTMLInputElement} field 
   * @param {string} message 
   */
  showFieldError(field, message) {
    const formField = field.closest('.form-field');
    if (!formField) return;

    formField.classList.add('form-field--error');
    field.setAttribute('aria-invalid', 'true');

    // Create or update error message
    let errorEl = formField.querySelector('.form-field__error');
    if (!errorEl) {
      errorEl = document.createElement('span');
      errorEl.className = 'form-field__error form-message form-message--small form-message--error';
      formField.appendChild(errorEl);
    }
    errorEl.textContent = message;
  }

  /**
   * Clears the error state for a field
   * @param {HTMLInputElement} field 
   */
  clearFieldError(field) {
    const formField = field.closest('.form-field');
    if (!formField) return;

    formField.classList.remove('form-field--error');
    field.removeAttribute('aria-invalid');

    const errorEl = formField.querySelector('.form-field__error');
    if (errorEl) {
      errorEl.remove();
    }
  }

  /**
   * Validates the entire form
   * @returns {boolean}
   */
  validateForm() {
    let isValid = true;
    this.form.querySelectorAll('input[required]').forEach(field => {
      if (!this.validateField(field)) {
        isValid = false;
      }
    });
    return isValid;
  }

  /**
   * Formats the phone number to ensure it has a country code
   * If no country code (starting with +), adds +1 (US)
   * @param {string} phone 
   * @returns {string}
   */
  formatPhoneNumber(phone) {
    if (!phone) return '';
    
    // Remove all non-digit characters except +
    let cleaned = phone.replace(/[^\d+]/g, '');
    
    // If it starts with +, keep the country code as-is
    if (cleaned.startsWith('+')) {
      return cleaned;
    }
    
    // If it starts with 1 and is 11 digits, assume it's already US format (1XXXXXXXXXX)
    if (cleaned.startsWith('1') && cleaned.length === 11) {
      return '+' + cleaned;
    }
    
    // Otherwise, add +1 (US country code)
    return '+1' + cleaned;
  }

  /**
   * Handles form submission
   * @param {Event} event 
   */
  async handleSubmit(event) {
    event.preventDefault();

    // Clear any previous status messages
    this.clearStatus();

    // Validate form
    if (!this.validateForm()) {
      this.showStatus('Please correct the errors above.', 'error');
      return;
    }

    // Collect form data
    const formData = new FormData(this.form);
    const registrationCode = (formData.get('registration_code') || '').trim().toUpperCase();

    const data = {
      first_name: formData.get('first_name'),
      last_name: formData.get('last_name'),
      email: formData.get('email'),
      phone: this.formatPhoneNumber(formData.get('phone')),
      home_store: formData.get('home_store') || '29R Properzi Way',
      wallet_type: this.walletType,
      terms_accepted: formData.get('terms_accepted') === 'yes',
      registration_code: registrationCode || undefined
    };

    // Remove undefined fields
    Object.keys(data).forEach(key => data[key] === undefined && delete data[key]);

    // Show loading state
    this.setLoading(true);

    try {
      // If no API endpoint is configured, simulate success for testing
      if (!this.apiEndpoint) {
        console.log('Bakehouse Wallet Registration - No API endpoint configured. Form data:', data);
        await this.simulateApiCall(data);
        return;
      }

      // Make the API call
      const response = await fetch(this.apiEndpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(data),
      });

      const result = await response.json();

      if (!response.ok || !result.success) {
        throw new Error(result.message || `Server error: ${response.status}`);
      }

      this.handleSuccess(result);

    } catch (error) {
      console.error('Bakehouse Wallet Registration Error:', error);
      this.showStatus(
        error.message || 'Something went wrong. Please try again.',
        'error'
      );
      this.setLoading(false);
    }
  }

  /**
   * Simulates an API call for testing purposes
   * @param {Object} data 
   */
  async simulateApiCall(data) {
    // Simulate network delay
    await new Promise(resolve => setTimeout(resolve, 1500));

    // Simulate success response matching the real API format
    const mockResponse = {
      success: true,
      customer_id: '123456789',
      wallet_type: this.walletType,
      wallet_pass_url: this.walletType === 'apple' 
        ? 'data:application/vnd.apple.pkpass;base64,UEsDBBQAAAA...' // Truncated mock
        : 'https://pay.google.com/gp/v/save/demo',
      is_new_customer: true,
      bonus_credits_added: 10,
      message: 'You will receive an email with your digital wallet info shortly.'
    };

    console.log('Bakehouse Wallet - Mock API Response:', mockResponse);
    this.handleSuccess(mockResponse);
  }

  /**
   * Handles successful registration
   * @param {Object} result - API response containing wallet pass info
   */
  handleSuccess(result) {
    this.setLoading(false);
    
    // Store the wallet pass URL
    this.walletPassUrl = result.wallet_pass_url;
    
    // Hide the form
    this.form.style.display = 'none';
    
    // Show success state
    if (this.successContainer) {
      this.successContainer.style.display = 'block';
      
      // Update success message with the one from API if provided
      if (result.message && this.successMessage) {
        this.successMessage.textContent = result.message;
      }
      
      // Set up the wallet button
      if (result.wallet_pass_url && this.addWalletButton) {
        const walletType = result.wallet_type || this.walletType;
        const isBase64 = result.wallet_pass_url.startsWith('data:');
        
        if (isBase64) {
          // For base64 data URLs (Apple Wallet), we need to trigger a download
          this.addWalletButton.href = '#';
          this.addWalletButton.addEventListener('click', (e) => {
            e.preventDefault();
            this.downloadWalletPass(result.wallet_pass_url, walletType);
          });
        } else {
          // For regular URLs (Google Wallet), just link directly
          this.addWalletButton.href = result.wallet_pass_url;
          this.addWalletButton.target = '_blank';
        }
        
        this.addWalletButton.style.display = 'inline-flex';
        
        // Update button text and icon based on wallet type
        if (walletType === 'apple') {
          this.walletText.textContent = 'Add to Apple Wallet';
          this.walletIcon.innerHTML = this.getAppleWalletIcon();
          this.addWalletButton.classList.add('bakehouse-wallet__add-wallet--apple');
        } else if (walletType === 'google') {
          this.walletText.textContent = 'Add to Google Wallet';
          this.walletIcon.innerHTML = this.getGoogleWalletIcon();
          this.addWalletButton.classList.add('bakehouse-wallet__add-wallet--google');
        } else {
          this.walletText.textContent = 'Add to Wallet';
        }
      }
    }

    // Scroll to success message
    this.successContainer?.scrollIntoView({ behavior: 'smooth', block: 'center' });

    // Track the registration event (for analytics)
    this.trackRegistration(result);
  }

  /**
   * Downloads a wallet pass from a base64 data URL
   * @param {string} dataUrl - The base64 data URL
   * @param {string} walletType - 'apple' or 'google'
   */
  downloadWalletPass(dataUrl, walletType) {
    try {
      // Parse the data URL
      const [header, base64Data] = dataUrl.split(',');
      const mimeType = header.match(/:(.*?);/)?.[1] || 'application/vnd.apple.pkpass';
      
      // Convert base64 to binary
      const binaryString = atob(base64Data);
      const bytes = new Uint8Array(binaryString.length);
      for (let i = 0; i < binaryString.length; i++) {
        bytes[i] = binaryString.charCodeAt(i);
      }
      
      // Create a blob
      const blob = new Blob([bytes], { type: mimeType });
      
      // Create download link
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = walletType === 'apple' ? 'wildgrain-wallet.pkpass' : 'wildgrain-wallet';
      
      // Trigger download
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      // Clean up
      URL.revokeObjectURL(url);
      
      console.log('Wallet pass download triggered');
    } catch (error) {
      console.error('Error downloading wallet pass:', error);
      // Fallback: try to open in new window
      window.open(dataUrl, '_blank');
    }
  }

  /**
   * Tracks the registration event for analytics
   * @param {Object} result 
   */
  trackRegistration(result) {
    const email = this.form.querySelector('[name="email"]')?.value;
    const firstName = this.form.querySelector('[name="first_name"]')?.value;
    const lastName = this.form.querySelector('[name="last_name"]')?.value;
    const phone = this.form.querySelector('[name="phone"]')?.value;
    const homeStore = this.form.querySelector('[name="home_store"]')?.value || '29R Properzi Way';
    
    // Klaviyo tracking
    if (window._learnq) {
      window._learnq.push(['identify', {
        '$email': email,
        '$first_name': firstName,
        '$last_name': lastName,
        '$phone_number': phone,
        'Bakehouse Wallet Member': true,
        'Home Store': homeStore,
        'Wallet Type': result.wallet_type || this.walletType,
        'First Time Wallet Generated': result.is_new_customer,
        'Bonus Credits': result.bonus_credits_added,
        'Wallet Retrieval URL': result.retrieval_url || ''
      }]);
      
      window._learnq.push(['track', 'Bakehouse Wallet Registered', {
        'Customer ID': result.customer_id,
        'Home Store': homeStore,
        'Wallet Type': result.wallet_type || this.walletType,
        'First Time Wallet Generated': result.is_new_customer,
        'Bonus Credits Added': result.bonus_credits_added,
        'Wallet Retrieval URL': result.retrieval_url || ''
      }]);
    }

    // Google Analytics / GTM
    if (window.dataLayer) {
      window.dataLayer.push({
        event: 'bakehouse_wallet_registration',
        customer_id: result.customer_id,
        home_store: homeStore,
        wallet_type: result.wallet_type || this.walletType,
        first_time_wallet_generated: result.is_new_customer,
        bonus_credits_added: result.bonus_credits_added
      });
    }
  }

  /**
   * Shows a status message
   * @param {string} message 
   * @param {string} type - 'success' | 'error'
   */
  showStatus(message, type = 'success') {
    if (!this.statusContainer) return;

    const alertClass = type === 'error' ? 'alert--error' : 'alert--success';
    const icon = type === 'error' ? this.getErrorIcon() : this.getSuccessIcon();

    this.statusContainer.innerHTML = `
      <div class="form-status-list">
        <div class="form-message alert ${alertClass} h6 blocks-radius" tabindex="-1">
          ${icon}
          ${message}
        </div>
      </div>
    `;
  }

  /**
   * Clears the status message
   */
  clearStatus() {
    if (this.statusContainer) {
      this.statusContainer.innerHTML = '';
    }
  }

  /**
   * Sets the loading state of the form
   * @param {boolean} isLoading 
   */
  setLoading(isLoading) {
    this.submitButton.disabled = isLoading;
    
    if (isLoading) {
      this.submitText.style.display = 'none';
      this.submitLoading.style.display = 'inline-flex';
      this.form.classList.add('bakehouse-wallet__form--loading');
    } else {
      this.submitText.style.display = 'inline';
      this.submitLoading.style.display = 'none';
      this.form.classList.remove('bakehouse-wallet__form--loading');
    }
  }

  /**
   * Returns the Apple Wallet icon SVG
   */
  getAppleWalletIcon() {
    return `<svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
      <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.81-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z"/>
    </svg>`;
  }

  /**
   * Returns the Google Wallet icon SVG
   */
  getGoogleWalletIcon() {
    return `<svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
      <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
      <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
      <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
      <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
    </svg>`;
  }

  /**
   * Returns the success icon SVG
   */
  getSuccessIcon() {
    return `<svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path fill-rule="evenodd" clip-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" fill="currentColor"/>
    </svg>`;
  }

  /**
   * Returns the error icon SVG
   */
  getErrorIcon() {
    return `<svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path fill-rule="evenodd" clip-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" fill="currentColor"/>
    </svg>`;
  }
}

// Register the custom element
if (!customElements.get('bakehouse-wallet-registration')) {
  customElements.define('bakehouse-wallet-registration', BakehouseWalletRegistration);
}
